/**
 * Shows the connected Greenhouse OAuth integration and its setup actions.
 *
 * The card keeps OAuth setup and removal here. Connected setup opens GreenhouseSetupDialog, while
 * the first-connect flow opens GreenhouseOauthSubdomainDialog.
 */

import React, { FC, useState } from 'react';

// eslint-disable-next-line no-restricted-syntax
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { useFeatureFlagBool } from '@modernloop/shared/feature-flag';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Box, Link, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { AtsType, IntegrationType } from 'src/generated/mloop-graphql';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import {
  submitGreenhouseOauthSubdomainUpdate,
  useGreenhouseOauthSubdomainUpdate,
  useIntegrationAuthorizeUrl,
  useIntegrationAuthorizeUrlMutation,
  useIntegrationDelete,
} from 'src/hooks/api/integration';
import { getAtsService } from 'src/hooks/atsService';

import { DisconectAtsDialog } from '../Dialogs/DisconnectAtsDialog';
import GreenhouseOauthSubdomainDialog from '../Dialogs/GreenhouseOauthSubdomainDialog';
import GreenhouseSetupDialog from '../Dialogs/GreenhouseSetupDialog';
import { GreenhouseSubdomain } from '../GreenhouseSubdomain';
import IntegrationCard from '../IntegrationCard';

import { ConnectAtsButton } from './ConnectAtsButton';
import { navigateToOauth } from './navigateToOauth';

const ORG_INTEGRATIONS_GREENHOUSE_OAUTH_QUERY = gql`
  query OrgIntegrationsGreenhouseOAuth {
    thisOrg {
      id
      integrations {
        id
        greenhouseOauth {
          subdomain
        }
      }
    }
  }
`;

export interface Props {
  disabled?: boolean;
  onRemove?: () => void;
}

/**
 * Integration card for Greenhouse. Shown in Settings > Integrations as the only Greenhouse card:
 * V3 OAuth is the one way to connect Greenhouse since the Harvest V1 API shut down.
 */
const GreenhouseOAuthCard: FC<Props> = ({ disabled = false, onRemove }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  const [openSubdomainDialog, setOpenSubdomainDialog] = useState(false);
  const [openSetupDialog, setOpenSetupDialog] = useState(false);
  // Product decision: the sectioned setup dialog ships behind the jobs-to-sync flag. With the flag
  // off, Manage setup keeps opening the plain subdomain dialog it opened before.
  const greenhouseJobSyncScopeEnabled = useFeatureFlagBool('org_greenhouse_job_sync_scope');

  const { data: greenhouseOAuthData, refetch } = useQuery(ORG_INTEGRATIONS_GREENHOUSE_OAUTH_QUERY);
  const greenhouseOauth = greenhouseOAuthData?.thisOrg?.integrations?.greenhouseOauth;

  const { data: authData } = useIntegrationAuthorizeUrl(
    {
      integration_type: IntegrationType.GreenhouseOauth,
      redirect_uri: '',
      login_hint: '',
    },
    { enabled: Boolean(greenhouseOauth) }
  );
  const createAuthorizeUrl = useIntegrationAuthorizeUrlMutation();
  const updateSubdomain = useGreenhouseOauthSubdomainUpdate();
  const deleteGreenhouseOauth = useIntegrationDelete(IntegrationType.GreenhouseOauth);

  const atsService = getAtsService(AtsType.Greenhouse);
  const name = 'Greenhouse (Harvest V3 - OAuth)';

  /** Submits a parsed subdomain through the connect or connected setup flow. */
  const submitSubdomain = async (subdomain: GreenhouseSubdomain, onSuccess?: () => void) => {
    try {
      if (greenhouseOauth) {
        await submitGreenhouseOauthSubdomainUpdate({
          subdomain,
          updateSubdomain,
          refetch,
          onSuccess,
          // Either dialog can host the subdomain form, so close both when the integration is gone.
          onNotFound: () => {
            setOpenSetupDialog(false);
            setOpenSubdomainDialog(false);
          },
          enqueueSnackbar,
          genericErrorMessage: t('common:shared.error.genericRetry'),
        });
        return;
      }

      const result = await createAuthorizeUrl.mutateAsync({
        integration_type: IntegrationType.GreenhouseOauth,
        redirect_uri: '',
        login_hint: '',
        greenhouse_subdomain: subdomain,
      });
      if (!result.url) {
        throw new Error();
      }
      navigateToOauth(result.url);
    } catch (error) {
      enqueueSnackbar(getExternalErrorMessage(error) || t('common:shared.error.genericRetry'), { variant: 'error' });
    }
  };

  return (
    <>
      <Box>
        <IntegrationCard
          dataTestId="greenhouse-oauth-card-integration-card"
          name={name}
          logo={atsService.logoUrl}
          onConnect={!greenhouseOauth ? () => setOpenSubdomainDialog(true) : undefined}
          onManageSetup={
            greenhouseOauth
              ? () => (greenhouseJobSyncScopeEnabled ? setOpenSetupDialog(true) : setOpenSubdomainDialog(true))
              : undefined
          }
          onReconnect={greenhouseOauth && authData ? () => window.open(authData?.url, '_blank') : undefined}
          onRemove={greenhouseOauth ? () => setOpenRemoveDialog(true) : undefined}
          disabled={disabled}
          disableManageSetup={disabled}
          connectButton={
            <ConnectAtsButton
              dataTestId="greenhouse-oauth-card-integration-card"
              handleConnect={() => setOpenSubdomainDialog(true)}
              disabled={disabled}
            />
          }
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          <Link href={HelpCenterUrl.GREENHOUSE_HARVEST_V3_INTEGRATION_STEPS} target="_blank" rel="noopener noreferrer">
            {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouse.harvestV3HelpLink')}
          </Link>
        </Typography>
      </Box>

      {openRemoveDialog && (
        <DisconectAtsDialog
          integrationName={name}
          onClose={() => setOpenRemoveDialog(false)}
          onRemove={() => {
            setOpenRemoveDialog(false);
            deleteGreenhouseOauth.mutate(undefined, {
              onSuccess: () => {
                refetch();
                onRemove?.();
              },
            });
          }}
        />
      )}
      {openSubdomainDialog && (
        <GreenhouseOauthSubdomainDialog
          initialSubdomain={greenhouseOauth?.subdomain}
          isSubmitting={createAuthorizeUrl.isLoading || updateSubdomain.isLoading}
          onClose={() => setOpenSubdomainDialog(false)}
          onSubmit={(subdomain) => submitSubdomain(subdomain, () => setOpenSubdomainDialog(false))}
        />
      )}
      {openSetupDialog && (
        <GreenhouseSetupDialog
          // Open on the first navigation item; the dialog falls back to Subdomain when Jobs is hidden.
          initialSection="JOBS_TO_SYNC"
          initialSubdomain={greenhouseOauth?.subdomain}
          disabled={disabled}
          isSubmitting={updateSubdomain.isLoading}
          onClose={() => setOpenSetupDialog(false)}
          onSubdomainSubmit={submitSubdomain}
        />
      )}
    </>
  );
};

export default GreenhouseOAuthCard;
